import { JSXElement, lazy } from 'solid-js';
import {
  EnvironmentPagePath,
  getSlug,
  MyAccountPagePath,
  PublicPagePath,
  WorkspacePagePath
} from '~/components/AppRouter/utils.ts';
import { Route } from '@solidjs/router';
import {
  getWorkspacePageComponent,
  LayoutTypes
} from '~/components/Layout/utils.tsx';
import withSession from '~/components/AppRouter/withSession.tsx';
import { withFeatureFlag } from '~/utils/features/featureFlag.tsx';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import { useIntl } from '@cookbook/solid-intl';
import { getLocalizedString } from '~/i18n/utils.ts';

const Signup = lazy(() => import('src/pages/UnAuthenticated/Signup'));
const Login = lazy(() => import('src/pages/UnAuthenticated/Login'));
const Onboarding = lazy(() => import('src/pages/Onboarding'));
const Home = lazy(() => import('~/pages/Home'));
const WorkspaceGettingStarted = lazy(
  () => import('~/pages/WorkspaceGettingStarted')
);
const WorkspaceQuickStart = lazy(() => import('~/pages/Quickstart'));
const WorkspaceQuickStartModule = lazy(
  () => import('~/pages/Quickstart/QuickStartModule')
);
const WorkspaceGeneralSettings = lazy(
  () => import('~/pages/WorkspaceGeneralSettings')
);
const ManageEnvironments = lazy(() => import('~/pages/ManageEnvironments'));
const TeamMembers = lazy(() => import('~/pages/TeamMembers'));
const Billing = lazy(() => import('~/pages/Billing'));
const BillingV2 = lazy(() => import('~/pages/Billing/BillingV2'));
const Organizations = lazy(() => import('~/pages/Organization'));
const Users = lazy(() => import('~/pages/Users'));
const UsersDetails = lazy(() => import('~/pages/Users/UserDetails'));
const UserAttributes = lazy(() => import('~/pages/UserAttributes'));
const OrganizationDetails = lazy(
  () => import('~/pages/Organization/OrganizationDetails')
);
const OrganizationDetailsWithSSO = lazy(
  () => import('~/pages/Organization/OrganizationDetailsWithSSO')
);
const OrganizationDetailsWithDirSync = lazy(
  () => import('~/pages/Organization/OrganizationDetailsWithDirectorySync')
);
const SocialConnections = lazy(
  () => import('~/pages/Authentication/Connections/SocialConnections')
);
const SocialConenctionDetails = lazy(
  () =>
    import(
      '~/pages/Authentication/Connections/SocialConnections/SocialConnectionDetails'
    )
);
const SingleSignonLandingPage = lazy(
  () => import('~/pages/Authentication/SingleSignOn')
);
const DirectorySyncLandingPage = lazy(
  () => import('~/pages/Authentication/DirectorySync')
);
const Customizations = lazy(() => import('~/pages/Customizations'));
const CustomizationsV2 = lazy(
  () => import('~/pages/Customizations/CustomizationsV2')
);
const Webhooks = lazy(() => import('~/pages/Webhooks'));
const Email = lazy(() => import('~/pages/Email'));
const WebhookDetails = lazy(() => import('~/pages/Webhooks/WebhookDetails'));
const ManageRoles = lazy(() => import('~/pages/DirectorySync/Roles'));
const ApiCredentials = lazy(() => import('~/pages/ApiCredentials'));
const MyProfile = lazy(() => import('~/pages/MyProfile'));

const FullStackAuthentication = lazy(
  () => import('~/pages/FullStackAuthentication')
);

const Authorization = lazy(() => import('~/pages/Authorization'));

const McpServers = lazy(() => import('~/pages/McpServers'));

const AddMcpServer = lazy(() => import('~/pages/McpServers/AddMcpServer'));

const McpServerDetails = lazy(
  () => import('~/pages/McpServers/McpServerDetails')
);

const Settings = lazy(() => import('~/pages/Settings'));

const ScalekitConnectIntegrations = lazy(
  () => import('~/pages/ScalekitConnect/Integrations')
);

const ScalekitConnectProviders = lazy(
  () => import('~/pages/ScalekitConnect/Integrations/Providers/Providers.tsx')
);

const ScalekitConnectedAppDetails = lazy(
  () => import('~/pages/ScalekitConnect/Integrations/ConnectedAppDetails')
);

const EnvironmentConnectedAccounts = lazy(
  () =>
    import(
      '~/pages/ScalekitConnect/Integrations/ConnectedAccounts/EnvironmentConnectedAccounts.tsx'
    )
);

const ScalekitConnectedAppToolPlayground = lazy(
  () =>
    import('~/pages/ScalekitConnect/Integrations/Tools/EnvironmentPlayground')
);

const AuthLogs = lazy(() => import('~/pages/AuthLogs'));

const AuthRequestEventPayload = lazy(
  () => import('~/pages/AuthLogs/handlers/AuthRequestEventPayload.tsx')
);

const AddRole = lazy(
  () => import('~/pages/Authorization/Roles/RolesForm/AddRole.tsx')
);

const EditRole = lazy(
  () => import('~/pages/Authorization/Roles/RolesForm/EditRole')
);

const CustomDomain = lazy(() => import('~/pages/CustomDomain'));

const Interceptors = lazy(
  () => import('~/pages/Interceptors/Interceptors.tsx')
);

const AddInterceptors = lazy(
  () => import('~/pages/Interceptors/AddInterceptors')
);

const InterceptorDetails = lazy(
  () => import('~/pages/Interceptors/InterceptorDetails')
);

const AgentAuth = lazy(
  () => import('~/pages/ScalekitConnect/commons/AgentAuth.tsx')
);

export default function WorkspaceAppRoutes(): JSXElement {
  const intl = useIntl();
  return (
    <Route path={getSlug.workspaceBaseNamespace()}>
      <Route
        path={`/`}
        component={getWorkspacePageComponent(
          withSession(Home),
          LayoutTypes.EMPTY_CANVAS
        )}
      />
      {/*<Route*/}
      {/*  path={`/`}*/}
      {/*  component={getWorkspacePageComponent(*/}
      {/*    getComponent('Home'),*/}
      {/*    LayoutTypes.TWO_COLUMN_WITH_NAV*/}
      {/*  )}*/}
      {/*/>*/}
      <Route
        path={getSlug.publicPage(PublicPagePath.LOGIN)}
        component={getWorkspacePageComponent(Login, LayoutTypes.EMPTY_CANVAS)}
      />
      <Route
        path={getSlug.publicPage(PublicPagePath.SIGNUP)}
        component={getWorkspacePageComponent(
          withFeatureFlag(Signup, FeatureFlagKeys.SIGNUP),
          LayoutTypes.EMPTY_CANVAS
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_AUTH_LOGS_EVENT_PAYLOAD
        )}
        component={getWorkspacePageComponent(
          withFeatureFlag(
            AuthRequestEventPayload,
            FeatureFlagKeys.AUTHENTICATION_LOGS
          ),
          LayoutTypes.EMPTY_CANVAS
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_GETTING_STARTED
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              WorkspaceGettingStarted,
              FeatureFlagKeys.GETTING_STARTED
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_QUICK_START
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(WorkspaceQuickStart, FeatureFlagKeys.QUICK_START)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.quick_start', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_QUICK_START_MODULE
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              WorkspaceQuickStartModule,
              FeatureFlagKeys.QUICK_START
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.quick_start', { intl })
        )}
      />
      <Route
        path={getSlug.workspace(WorkspacePagePath.WORKSPACE_ONBOARDING)}
        component={getWorkspacePageComponent(
          withFeatureFlag(Onboarding, FeatureFlagKeys.SIGNUP),
          LayoutTypes.EMPTY_CANVAS
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_ORGANIZATIONS
        )}
        component={getWorkspacePageComponent(
          withSession(Organizations),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.organization', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_USERS
        )}
        component={getWorkspacePageComponent(
          withSession(withFeatureFlag(Users, FeatureFlagKeys.USERS)),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.users', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_USER_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(withFeatureFlag(UsersDetails, FeatureFlagKeys.USERS)),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.users', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_ORGANIZATION_DETAILS_USER_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(withFeatureFlag(UsersDetails, FeatureFlagKeys.USERS)),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.organization', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_ORGANIZATION_DETAILS_OVERVIEW
        )}
        component={getWorkspacePageComponent(
          withSession(OrganizationDetails),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.organization', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_ORGANIZATION_DETAILS_CONNECTION_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(OrganizationDetailsWithSSO),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.organization', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_ORGANIZATION_DETAILS_DIRECTORY_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              OrganizationDetailsWithDirSync,
              FeatureFlagKeys.DIRECTORY_SYNC
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.organization', { intl })
        )}
      />
      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION_TABS
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION_METHODS
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_SOCIAL_LOGIN_DETAILS
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              FullStackAuthentication,
              FeatureFlagKeys.AUTHENTICATION_METHODS
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.authentication', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_SOCIAL_CONNECTIONS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              SocialConnections,
              FeatureFlagKeys.SOCIAL_CONNECTION
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.social_login', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_SOCIAL_CONNECTION_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              SocialConenctionDetails,
              FeatureFlagKeys.SOCIAL_CONNECTION
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.social_login', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_SINGLE_SIGN_ON
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              SingleSignonLandingPage,
              FeatureFlagKeys.SINGLE_SIGNON
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.sso', { intl })
        )}
      />
      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_AUTHORIZATION
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_AUTHORIZATION_TABS
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(Authorization, FeatureFlagKeys.FSA_AUTHORIZATION)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.authorization', { intl })
        )}
      />
      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_ADD_ROLE
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_ORGANIZATION_ADD_ROLES
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(AddRole, FeatureFlagKeys.FSA_AUTHORIZATION)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('app_title.add_role_app_title', { intl })
        )}
      />
      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_ROLE_DETAILS
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_ORGANIZATION_ROLE_DETAILS
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(EditRole, FeatureFlagKeys.FSA_AUTHORIZATION)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.roles', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_DIRECTORY_SYNC
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              DirectorySyncLandingPage,
              FeatureFlagKeys.DIRECTORY_SYNC
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.directory_sync', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_USER_ATTRIBUTES
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              UserAttributes,
              FeatureFlagKeys.USER_MANAGEMENT_SETTINGS
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.user_attributes', { intl })
        )}
      />
      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_CUSTOMIZATIONS
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_CUSTOMIZATION_TABS
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              Customizations,
              FeatureFlagKeys.PORTAL_CUSTOMIZATION
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.customization', { intl })
        )}
      />
      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_BRANDING
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_BRANDING_BY_WORKFLOW_TYPE
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              CustomizationsV2,
              FeatureFlagKeys.FSA_CUSTOMIZATION_ENABLED
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.customization', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_CUSTOM_DOMAIN
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(CustomDomain, FeatureFlagKeys.FSA_SETTINGS)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.custom_domain_title', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_WEBHOOKS
        )}
        component={getWorkspacePageComponent(
          withSession(withFeatureFlag(Webhooks, FeatureFlagKeys.WEBHOOKS)),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.webhooks', { intl })
        )}
      />
      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_EMAILS
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_EMAILS_TABS
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_EMAILS_TEMPLATE_DETAILS
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(Email, FeatureFlagKeys.EMAIL_CONFIGURATION)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.email_configuration', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_WEBHOOK_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(WebhookDetails, FeatureFlagKeys.WEBHOOKS)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.webhooks', { intl })
        )}
      />

      {/*  Scalekit Connect routes */}
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.SCALEKIT_CONNECT_INTEGRATIONS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              ScalekitConnectIntegrations,
              FeatureFlagKeys.SCALEKIT_CONNECT
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.connect_integrations', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.SCALEKIT_CONNECTED_PROVIDERS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              ScalekitConnectProviders,
              FeatureFlagKeys.SCALEKIT_CONNECT
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.integration_providers', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.SCALEKIT_CONNECT_INTEGRATION_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              ScalekitConnectedAppDetails,
              FeatureFlagKeys.SCALEKIT_CONNECT
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.connect_integrations', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.SCALEKIT_CONNECT_TOOL_EXECUTION_PLAYGROUND
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              ScalekitConnectedAppToolPlayground,
              FeatureFlagKeys.SCALEKIT_CONNECT
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.playground', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.SCALEKIT_CONNECTED_ACCOUNTS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              EnvironmentConnectedAccounts,
              FeatureFlagKeys.SCALEKIT_CONNECT
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.connected_accounts', { intl })
        )}
      />
      {/*  Scalekit Connect routes end */}

      {/* Scalekit Agent Auth New routes start */}

      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.SCALEKIT_AGENT_AUTH
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.SCALEKIT_AGENT_AUTH_TABS
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(AgentAuth, FeatureFlagKeys.SCALEKIT_AGENT_AUTH)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.agent_auth', { intl })
        )}
      />

      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.SCALEKIT_AGENT_AUTH_CONNECTED_PROVIDERS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              ScalekitConnectProviders,
              FeatureFlagKeys.SCALEKIT_AGENT_AUTH
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.integration_providers', { intl })
        )}
      />

      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.SCALEKIT_AGENT_AUTH_CONNECTION_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(
              ScalekitConnectedAppDetails,
              FeatureFlagKeys.SCALEKIT_AGENT_AUTH
            )
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.connect_integrations', { intl })
        )}
      />

      {/* Scalekit Agent Auth New routes end */}

      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_AUTHENTICATION_LOGS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(AuthLogs, FeatureFlagKeys.AUTHENTICATION_LOGS)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.auth_logs', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_INTERCEPTORS
        )}
        component={getWorkspacePageComponent(
          withFeatureFlag(Interceptors, FeatureFlagKeys.INTERCEPTORS),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.interceptors', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_ADD_INTERCEPTORS
        )}
        component={getWorkspacePageComponent(
          withFeatureFlag(AddInterceptors, FeatureFlagKeys.INTERCEPTORS),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('app_title.add_interceptor_app_title', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_INTERCEPTOR_DETAILS
        )}
        component={getWorkspacePageComponent(
          withFeatureFlag(InterceptorDetails, FeatureFlagKeys.INTERCEPTORS),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('app_title.interceptor_details_app_title', {
            intl
          })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_ROLES
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(ManageRoles, FeatureFlagKeys.DIRECTORY_SYNC)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.roles', { intl })
        )}
      />
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_API_CREDENTIALS
        )}
        component={getWorkspacePageComponent(
          withSession(ApiCredentials),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.api_credentials', { intl })
        )}
      />
      <Route
        path={[
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_SETTINGS
          ),
          getSlug.environmentById(
            undefined,
            EnvironmentPagePath.ENVIRONMENT_SETTINGS_TABS
          )
        ]}
        component={getWorkspacePageComponent(
          withSession(withFeatureFlag(Settings, FeatureFlagKeys.FSA_SETTINGS)),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.settings', { intl })
        )}
      />
      <Route
        path={getSlug.workspace(WorkspacePagePath.WORKSPACE_GENERAL)}
        component={getWorkspacePageComponent(
          withSession(WorkspaceGeneralSettings),
          LayoutTypes.TWO_COLUMN_WITHOUT_NAV,
          getLocalizedString('page_titles.workspace_general', { intl })
        )}
      />
      <Route
        path={getSlug.workspace(
          WorkspacePagePath.WORKSPACE_MANAGE_ENVIRONMENTS
        )}
        component={getWorkspacePageComponent(
          withSession(ManageEnvironments),
          LayoutTypes.TWO_COLUMN_WITHOUT_NAV,
          getLocalizedString('page_titles.manage_environments', { intl })
        )}
      />
      <Route
        path={getSlug.workspace(WorkspacePagePath.WORKSPACE_MEMBERS)}
        component={getWorkspacePageComponent(
          withSession(TeamMembers),
          LayoutTypes.TWO_COLUMN_WITHOUT_NAV,
          getLocalizedString('page_titles.members', { intl })
        )}
      />
      <Route
        path={getSlug.workspace(WorkspacePagePath.WORKSPACE_BILLING_V1)}
        component={getWorkspacePageComponent(
          withSession(withFeatureFlag(Billing, FeatureFlagKeys.BILLING)),
          LayoutTypes.TWO_COLUMN_WITHOUT_NAV,
          getLocalizedString('page_titles.billing', { intl })
        )}
      />
      <Route
        path={getSlug.workspace(WorkspacePagePath.WORKSPACE_BILLING)}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(BillingV2, FeatureFlagKeys.WORKSPACE_BILLING)
          ),
          LayoutTypes.TWO_COLUMN_WITHOUT_NAV,
          getLocalizedString('page_titles.billing', { intl })
        )}
      />
      <Route
        path={getSlug.myAccount(MyAccountPagePath.MYACCOUNT_PROFILE)}
        component={getWorkspacePageComponent(
          withSession(MyProfile),
          LayoutTypes.TWO_COLUMN_WITHOUT_NAV,
          getLocalizedString('page_titles.my_profile', { intl })
        )}
      />

      {/*  M2M route definitions  */}
      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_MCP_SERVERS_LIST
        )}
        component={getWorkspacePageComponent(
          withSession(withFeatureFlag(McpServers, FeatureFlagKeys.MCP_SERVERS)),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.mcp_servers', { intl })
        )}
      />

      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_ADD_MCP_SERVER
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(AddMcpServer, FeatureFlagKeys.MCP_SERVERS)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.add_mcp_server', { intl })
        )}
      />

      <Route
        path={getSlug.environmentById(
          undefined,
          EnvironmentPagePath.ENVIRONMENT_MCP_SERVER_DETAILS
        )}
        component={getWorkspacePageComponent(
          withSession(
            withFeatureFlag(McpServerDetails, FeatureFlagKeys.MCP_SERVERS)
          ),
          LayoutTypes.TWO_COLUMN_WITH_NAV,
          getLocalizedString('page_titles.mcp_servers', { intl })
        )}
      />
      {/*  M2M route definitions end  */}
    </Route>
  );
}
